import React, { useState } from 'react';
import { Alert, Button, Modal, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { RightCircleOutlined } from '@ant-design/icons';
import {
  ApplyToInternship,
  GetInternshipOfferById,
} from '../../helper/apiHelper';
import type { DetailedInternship } from '../../Types/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useProcessStartSettings } from '../../hooks/useProcessStartSettings';
import { ProcessBlockedBanner } from '../ProcessBlockedBanner';
import { formatDate, parseDate } from '../../Utils/date';

export type ApplyableStatus =
  | 'Allowed'
  | 'HisOwnCompany'
  | 'HeIsntOnThatStudyLine';

interface InternshipApplicationComponentProps {
  internshipId: number;
  sessionId: number;
  organization: string;
  removeInternship: (id: number) => void;
  applyable: ApplyableStatus;
  studyLineName: string;
}

const GetDateOnlyFromDateTime = (dateTime: Date): string => {
  const date = parseDate(dateTime);
  // check if date is not 01/01/1000
  if (!date || date.year() === 1) {
    return 'N/A';
  }
  return formatDate(dateTime);
};

const InternshipApplicationComponent = ({
  internshipId,
  sessionId,
  organization,
  removeInternship,
  applyable,
  studyLineName,
}: InternshipApplicationComponentProps): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { internshipApplicationEnabled } = useProcessStartSettings();
  const applyButtonDisabled =
    applyable !== 'Allowed' || !internshipApplicationEnabled;

  const { data: internship } = useQuery({
    queryKey: [...QUERY_KEYS.INTERNSHIP, internshipId],
    queryFn: async () => {
      return await GetInternshipOfferById(internshipId.toString());
    },
  });

  const showModal = (): void => {
    setOpen(true);
  };

  const applyMutation = useMutation({
    mutationFn: async () => {
      await ApplyToInternship(
        String(loggedInUser?.id),
        internship ? internship.id.toString() : '0',
        sessionId
      );
    },
    onSuccess: () => {
      setOpen(false);
      removeInternship(internship ? internship.id : 0);
      messageApi.open({
        type: 'success',
        content: t('internshipApplicationComp.applySuccess'),
      });

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STUDENT_DATA });
    },
    onError: () => {
      messageApi.open({
        type: 'error',
        content: t('internshipApplicationComp.applyError'),
      });
    },
  });

  const isPaid = (internship: DetailedInternship | undefined): string => {
    if (internship) {
      return internship.paid
        ? t('internshipApplicationComp.yes')
        : t('internshipApplicationComp.no');
    }
    return t('internshipApplicationComp.na');
  };

  const warningApplyableMessage = (): React.ReactNode => {
    switch (applyable) {
      case 'HisOwnCompany':
        return (
          <Alert
            style={{ marginRight: 10 }}
            message={t(
              'internshipApplicationComp.youCannotApplyForYourOwnInternship'
            )}
            type="warning"
          />
        );

      case 'HeIsntOnThatStudyLine':
        return (
          <Alert
            style={{ marginRight: 10 }}
            message={
              t('internshipApplicationComp.alertMessage') +
              `: ${studyLineName}.`
            }
            type="warning"
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {contextHolder}
      <Button size="large" type="text" onClick={showModal}>
        {t('internshipApplicationComp.lrnMore')}
        <RightCircleOutlined />
      </Button>
      <Modal
        title={internship ? internship.name : t('internshipApplicationComp.na')}
        open={open}
        onOk={() => applyMutation.mutate()}
        onCancel={() => setOpen(false)}
        key={internshipId}
        footer={[
          <div
            key="div-footer"
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'right',
            }}
          >
            {/* Warning message if student cannot apply to this internship */}
            {warningApplyableMessage()}

            <Button
              disabled={applyButtonDisabled}
              style={{ alignItems: 'center', height: 40 }}
              key="apply"
              type="primary"
              onClick={() => applyMutation.mutate()}
            >
              {t('internshipApplicationComp.apply')}
            </Button>
          </div>,
        ]}
      >
        {!internshipApplicationEnabled && (
          <ProcessBlockedBanner
            style={{ marginBottom: 16 }}
            message={t('processStartSettings.internshipApplicationBlocked')}
          />
        )}
        <Typography.Text strong>
          {t('internshipApplicationComp.organization')}: {organization}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.descr')}:{' '}
          {internship
            ? internship.description
            : t('internshipApplicationComp.na')}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.startDate')}:{' '}
          {internship
            ? GetDateOnlyFromDateTime(internship.startTime)
            : t('internshipApplicationComp.na')}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.endDate')}:{' '}
          {internship
            ? GetDateOnlyFromDateTime(internship.endTime)
            : t('internshipApplicationComp.na')}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.paid')}: {isPaid(internship)}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.applNum')}:{' '}
          {internship ? internship.numberOfPositions : 0}
        </Typography.Text>
        <br />
        <Typography.Text strong>
          {t('internshipApplicationComp.internshipSessions')}:{' '}
          {internship
            ? internship.internshipSessions.map((s) => s.description).join(' ')
            : '-'}
        </Typography.Text>
      </Modal>
    </>
  );
};

export default InternshipApplicationComponent;
